import classnames from 'classnames';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useImageSizeObserver } from '@/hooks/useImageSizeObserver';

export type IconSizes =
  | 'x-small'
  | 'small'
  | 'medium'
  | 'large'
  | 'x-large'
  | 'xx-large'
  | 'jumbo'
  | 'x-jumbo'
  | 'xx-jumbo';

export interface IconProps {
  name: string;
  size?: IconSizes;
  className?: string;
  url?: string;
}

// Cache to store loaded icons
const iconCache: Record<string, React.ElementType> = {};

const Icon = ({ name, size = 'large', className, url }: IconProps) => {
  const [SvgIcon, setSvgIcon] = useState<React.ElementType | null>(iconCache[name] || null);

  const imageRef = useRef<HTMLImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { width, height } = useImageSizeObserver([imageRef, svgRef]);

  // Memoize className string
  const iconClasses = useMemo(
    () =>
      // TODO: replace icon sizes tw class 'size-{}' with 'h-{}' and add 'w-auto' so non-square icons scale properly
      // (icons may need to be updated to accomodate this change)
      classnames(
        {
          'size-24': size === 'xx-jumbo',
          'size-20': size === 'x-jumbo',
          'size-12': size === 'jumbo',
          'size-10': size === 'xx-large',
          'size-8': size === 'x-large',
          'size-6': size === 'large',
          'size-5': size === 'medium',
          'size-4 min-w-4': size === 'small',
          'size-3': size === 'x-small'
        },
        className
      ),
    [size, className]
  );

  // Only load if not in cache
  useEffect(() => {
    if (iconCache[name]) {
      setSvgIcon(iconCache[name]);
      return;
    }

    let isMounted = true;

    async function loadIcon() {
      try {
        // Dynamically import the SVG as a React component
        if (name) {
          const icon = await import(`../../assets/icons/${name}.svg?react`);
          if (icon) {
            const ForwardedIcon = forwardRef<SVGSVGElement>((props, ref) => {
              const ImportedIcon = icon.default;
              return <ImportedIcon {...props} ref={ref} />;
            });
            ForwardedIcon.displayName = `Icon(${name})`;

            iconCache[name] = ForwardedIcon;
            if (isMounted) {
              setSvgIcon(() => ForwardedIcon);
            }
          }
        }
      } catch (error) {
        console.error(`No icon found for name: ${name}`, error);
        setSvgIcon(null);
      }
    }

    if (!url) loadIcon();

    return () => {
      isMounted = false;
    };
  }, [name, url]);

  if (url) {
    return (
      <img
        src={url}
        className={iconClasses}
        alt={name}
        height={height}
        width={width}
        ref={imageRef}
      />
    );
  }

  if (!SvgIcon) {
    return <span className={classnames('inline-block', iconClasses)} />;
  }

  return (
    <SvgIcon
      ref={svgRef}
      className={iconClasses}
      role="presentation"
      height={height}
      width={width}
    />
  );
};

export default Icon;
